import React, { useState } from 'react'

import type {
  TDerivedMetricExpression,
  TDerivedMetricFactTableOptionDto,
  TDerivedMetricOperand,
  TDerivedMetricOperation
} from '@homepal/api-contract'

import Stack from 'components_new/atoms/Stack'

import FormulaBlock from 'components_new/molecules/FormulaBlock'
import FormulaAddButton from 'components_new/molecules/FormulaBlock/AddButton'
import FormulaConstantField from 'components_new/molecules/FormulaBlock/ConstantField'
import FormulaOperandSelect from 'components_new/molecules/FormulaBlock/OperandSelect'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import SelectSearch from 'components_new/molecules/SelectSearch'

import {
  EMPTY_EXPRESSION,
  EMPTY_METRIC_OPERAND,
  MAX_EXPRESSION_DEPTH,
  OPERATION_LABELS
} from '../utils'

type OperandType = TDerivedMetricOperand['type']

const OPERATIONS: TDerivedMetricOperation[] = ['/', '*', '+', '-']

function emptyOperand(type: OperandType): TDerivedMetricOperand {
  if (type === 'constant') {
    return { type: 'constant', value: -1 }
  }

  if (type === 'expression') {
    return { type: 'expression', expression: EMPTY_EXPRESSION }
  }

  return EMPTY_METRIC_OPERAND
}

/**
 * Whether the operand is still the placeholder both sides start out as, which
 * is what the stored format has instead of an empty operand.
 */
function isUnset(operand: TDerivedMetricOperand): boolean {
  return (
    operand.type === 'metric' &&
    !operand.kpi_template_id &&
    operand.slice_ids.length === 0
  )
}

interface OperandProps {
  depth: number
  factTable: TDerivedMetricFactTableOptionDto | null
  onChange: (operand: TDerivedMetricOperand) => void
  operand: TDerivedMetricOperand
}

/**
 * One side of the expression: a metric narrowed by slices, a constant, or a
 * nested expression. What it is changes from the pencil in the field, the way
 * an economy KPI's formula works.
 */
const Operand = (props: OperandProps) => {
  const { depth, factTable, onChange, operand } = props

  // The stored format has no empty operand, so a side that has been given a
  // type is remembered here until it carries a value.
  const [typed, setTyped] = useState(!isUnset(operand))

  const change = (type: OperandType) => {
    setTyped(true)
    onChange(emptyOperand(type))
  }

  const typeOptions: OptionProps[] = [
    {
      iconName: 'TrendingUpOutlined',
      onClick: () => change('metric'),
      title: 'Mätetal'
    },
    {
      iconName: 'NumbersOutlined',
      onClick: () => change('constant'),
      title: 'Konstant'
    },
    {
      // One level deeper than this and the API would reject the definition.
      disabled: depth >= MAX_EXPRESSION_DEPTH,
      iconName: 'ViewCarouselOutlined',
      onClick: () => change('expression'),
      title: 'Uttryck',
      tooltip: 'Ett uttryck kan bara innehålla ett nästlat uttryck'
    }
  ]

  if (isUnset(operand) && !typed) {
    return <FormulaAddButton options={typeOptions} tooltip="Lägg till" />
  }

  if (operand.type === 'expression') {
    return (
      <ExpressionEditor
        depth={depth + 1}
        expression={operand.expression}
        factTable={factTable}
        onChange={(expression) => onChange({ ...operand, expression })}
        onRemove={() => {
          setTyped(false)
          onChange(EMPTY_METRIC_OPERAND)
        }}
      />
    )
  }

  if (operand.type === 'constant') {
    return (
      <FormulaConstantField
        onChange={(value) =>
          onChange({ type: 'constant', value: parseFloat(value) })
        }
        typeOptions={typeOptions}
        value={Number.isFinite(operand.value) ? operand.value : ''}
      />
    )
  }

  const metricOptions = (factTable?.metrics ?? []).map((metric) => ({
    id: metric.id,
    title: metric.name
  }))

  const sliceOptions = (factTable?.slices ?? []).map((slice) => ({
    id: slice.id,
    title: slice.name
  }))

  return (
    <Stack direction="column" sx={{ gap: 1 }}>
      <FormulaOperandSelect
        onChange={(id) => onChange({ ...operand, kpi_template_id: id })}
        options={metricOptions}
        placeholder="Välj mätetal…"
        selectedId={operand.kpi_template_id || null}
        typeOptions={typeOptions}
      />
      <SelectSearch
        multiple={true}
        noGutter={true}
        onChange={(value) =>
          onChange({
            ...operand,
            slice_ids: value.map((option) => option.id)
          })
        }
        options={sliceOptions}
        placeholder="Hela utfallet"
        selected={sliceOptions.filter((option) =>
          operand.slice_ids.includes(option.id)
        )}
        size="small"
      />
    </Stack>
  )
}

export interface ExpressionEditorProps {
  /** 1 for the outermost expression. */
  depth?: number
  expression: TDerivedMetricExpression
  /** null until a fact table is chosen — the operands come from it. */
  factTable: TDerivedMetricFactTableOptionDto | null
  onChange: (expression: TDerivedMetricExpression) => void
  /** Given for a nested expression, which can be taken away again. */
  onRemove?: () => void
}

/**
 * The expression as it is stored: two operands and an operation, where an
 * operand can itself be an expression.
 *
 * The nesting is what makes sign handling expressible — `debiterat ÷
 * (kostnadsutfall × −1)` puts the constant on the operand that needs it
 * instead of after the whole quotient.
 */
const ExpressionEditor = (props: ExpressionEditorProps) => {
  const { depth = 1, expression, factTable, onChange, onRemove } = props

  return (
    <FormulaBlock
      left={
        <Operand
          depth={depth}
          factTable={factTable}
          onChange={(left) => onChange({ ...expression, left })}
          operand={expression.left}
        />
      }
      onChangeOperation={(operation) =>
        onChange({
          ...expression,
          operation: operation as TDerivedMetricOperation
        })
      }
      onRemove={onRemove}
      operation={expression.operation}
      operations={OPERATIONS.map((operation) => ({
        id: operation,
        title: OPERATION_LABELS[operation]
      }))}
      right={
        <Operand
          depth={depth}
          factTable={factTable}
          onChange={(right) => onChange({ ...expression, right })}
          operand={expression.right}
        />
      }
    />
  )
}

export default ExpressionEditor
